
/**
 * Defines a supported unit
 */
export interface ApplicationRateInfo {

    /** Id */
    id: ApplicationRateUnit

    /** Correspoding total unit */
    totalUnit: ApplicationTotalUnit

    /** Label to use for rates */
    rate: string

    /** Label to use for totals */
    total: string,

    /** Weight of 1 unit in kg */
    weight?: number

    /** Number of seeds */
    seeds?: number

}


export function getApplicationRateUnit(unit: ApplicationRateUnit) {
    return unit ? applicationUnits[unit] : undefined
}

export function getTotalUnitFromRateUnit(unit: ApplicationRateUnit) {
    const u = getApplicationRateUnit(unit)
    return u?.totalUnit
}

export function getApplicationTotalUnitName(unit: ApplicationTotalUnit) {
    return Object.values(applicationUnits).find(u => u.totalUnit === unit).total
}

/*
* The code below can be generated using "omnia.cmd ts-application-units"
*/

export enum ApplicationRateUnit {

    // kg/ha
    kg_ha = 1,

    // t/ha
    t_ha = 2,

    // l/ha
    l_ha = 3,

    // m³/ha
    m3_ha = 4,

    // seeds/m²
    seeds_m2 = 5,

    // seeds/ha
    seeds_ha = 6,

    // g/ha
    g_ha = 7,

    // ml/ha
    ml_ha = 8,

    // l/l
    l_l = 9,

    // l/100l
    l_100l = 10,

    // units/ha
    seedsunits_ha = 11,

    // bags/ha
    bags_ha = 12,

    // plants/ha
    plants_ha = 13,

    // trays/ha
    trays_ha = 14,

    // 1K seeds/ha
    seeds_1K_ha = 15,

    // 10K seeds/ha
    seeds_10K_ha = 16,

    // 1M seeds/ha
    seeds_1M_ha = 17,

    // tubers/ha
    tubers_ha = 18,

    // bulbs/ha
    bulbs_ha = 19,

    // ml/100l
    ml_100l = 20,

    // g/100l
    g_100l = 21,

    // kg/100l
    kg_100l = 22,

}

export enum ApplicationTotalUnit {

    // kg
    kg = 1,

    // t
    t = 2,

    // l
    l = 3,

    // m³
    m3 = 4,

    // seeds
    seeds = 5,

    // g
    g = 6,

    // ml
    ml = 7,

    // units
    seedsunits = 8,

    // bags
    bags = 9,

    // plants
    plants = 10,

    // trays
    trays = 11,

    // 1K seeds
    seeds_1K = 12,

    // 10K seeds
    seeds_10K = 13,

    // 1M seeds
    seeds_1M = 14,

    // tubers
    tubers = 16,

    // bulbs
    bulbs = 17,

}


export const applicationUnits: { [id in ApplicationRateUnit]: ApplicationRateInfo } = {

    [ApplicationRateUnit.kg_ha]: {
        id: ApplicationRateUnit.kg_ha,
        totalUnit: ApplicationTotalUnit.kg,
        rate: "kg/ha",
        total: "kg",
        weight: 1,
        seeds: undefined,
    },

    [ApplicationRateUnit.t_ha]: {
        id: ApplicationRateUnit.t_ha,
        totalUnit: ApplicationTotalUnit.t,
        rate: "t/ha",
        total: "t",
        weight: 1000,
        seeds: undefined,
    },

    [ApplicationRateUnit.g_ha]: {
        id: ApplicationRateUnit.g_ha,
        totalUnit: ApplicationTotalUnit.g,
        rate: "g/ha",
        total: "g",
        weight: 0.001,
        seeds: undefined,
    },

    [ApplicationRateUnit.ml_ha]: {
        id: ApplicationRateUnit.ml_ha,
        totalUnit: ApplicationTotalUnit.ml,
        rate: "ml/ha",
        total: "ml",
        weight: undefined,
        seeds: undefined,
    },

    [ApplicationRateUnit.l_ha]: {
        id: ApplicationRateUnit.l_ha,
        totalUnit: ApplicationTotalUnit.l,
        rate: "l/ha",
        total: "l",
        weight: undefined,
        seeds: undefined,
    },

    [ApplicationRateUnit.m3_ha]: {
        id: ApplicationRateUnit.m3_ha,
        totalUnit: ApplicationTotalUnit.m3,
        rate: "m³/ha",
        total: "m³",
        weight: undefined,
        seeds: undefined,
    },

    [ApplicationRateUnit.l_l]: {
        id: ApplicationRateUnit.l_l,
        totalUnit: ApplicationTotalUnit.l,
        rate: "l/l",
        total: "l",
        weight: undefined,
        seeds: undefined,
    },

    [ApplicationRateUnit.l_100l]: {
        id: ApplicationRateUnit.l_100l,
        totalUnit: ApplicationTotalUnit.l,
        rate: "l/100l",
        total: "l",
        weight: undefined,
        seeds: undefined,
    },

    [ApplicationRateUnit.kg_100l]: {
        id: ApplicationRateUnit.kg_100l,
        totalUnit: ApplicationTotalUnit.kg,
        rate: "kg/100l",
        total: "kg",
        weight: undefined,
        seeds: undefined,
    },

    [ApplicationRateUnit.ml_100l]: {
        id: ApplicationRateUnit.ml_100l,
        totalUnit: ApplicationTotalUnit.ml,
        rate: "ml/100l",
        total: "ml",
        weight: undefined,
        seeds: undefined,
    },

    [ApplicationRateUnit.g_100l]: {
        id: ApplicationRateUnit.g_100l,
        totalUnit: ApplicationTotalUnit.g,
        rate: "g/100l",
        total: "g",
        weight: undefined,
        seeds: undefined,
    },

    [ApplicationRateUnit.seeds_m2]: {
        id: ApplicationRateUnit.seeds_m2,
        totalUnit: ApplicationTotalUnit.seeds,
        rate: "seeds/m²",
        total: "seeds",
        weight: undefined,
        seeds: 1,
    },

    [ApplicationRateUnit.seeds_ha]: {
        id: ApplicationRateUnit.seeds_ha,
        totalUnit: ApplicationTotalUnit.seeds,
        rate: "seeds/ha",
        total: "seeds",
        weight: undefined,
        seeds: 1,
    },

    [ApplicationRateUnit.seeds_1K_ha]: {
        id: ApplicationRateUnit.seeds_1K_ha,
        totalUnit: ApplicationTotalUnit.seeds_1K,
        rate: "1K seeds/ha",
        total: "1K seeds",
        weight: undefined,
        seeds: 1000,
    },

    [ApplicationRateUnit.seeds_10K_ha]: {
        id: ApplicationRateUnit.seeds_10K_ha,
        totalUnit: ApplicationTotalUnit.seeds_10K,
        rate: "10K seeds/ha",
        total: "10K seeds",
        weight: undefined,
        seeds: 10000,
    },

    [ApplicationRateUnit.seeds_1M_ha]: {
        id: ApplicationRateUnit.seeds_1M_ha,
        totalUnit: ApplicationTotalUnit.seeds_1M,
        rate: "1M seeds/ha",
        total: "1M seeds",
        weight: undefined,
        seeds: 1000000,
    },

    [ApplicationRateUnit.seedsunits_ha]: {
        id: ApplicationRateUnit.seedsunits_ha,
        totalUnit: ApplicationTotalUnit.seedsunits,
        rate: "units/ha",
        total: "units",
        weight: undefined,
        seeds: 50000,
    },

    [ApplicationRateUnit.bags_ha]: {
        id: ApplicationRateUnit.bags_ha,
        totalUnit: ApplicationTotalUnit.bags,
        rate: "bags/ha",
        total: "bags",
        weight: undefined,
        seeds: undefined,
    },

    [ApplicationRateUnit.plants_ha]: {
        id: ApplicationRateUnit.plants_ha,
        totalUnit: ApplicationTotalUnit.plants,
        rate: "plants/ha",
        total: "plants",
        weight: undefined,
        seeds: undefined,
    },

    [ApplicationRateUnit.trays_ha]: {
        id: ApplicationRateUnit.trays_ha,
        totalUnit: ApplicationTotalUnit.trays,
        rate: "trays/ha",
        total: "trays",
        weight: undefined,
        seeds: undefined,
    },

    [ApplicationRateUnit.tubers_ha]: {
        id: ApplicationRateUnit.tubers_ha,
        totalUnit: ApplicationTotalUnit.tubers,
        rate: "tubers/ha",
        total: "tubers",
        weight: undefined,
        seeds: undefined,
    },

    [ApplicationRateUnit.bulbs_ha]: {
        id: ApplicationRateUnit.bulbs_ha,
        totalUnit: ApplicationTotalUnit.bulbs,
        rate: "bulbs/ha",
        total: "bulbs",
        weight: undefined,
        seeds: undefined,
    },

}